/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import OrbLoader from "../../components/common/OrbLoader";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import ConfirmationModal from "../../components/modals/ConfirmationModal";
import { selectSidebarCollapsed } from "../../store/slices/app/appSelector";
import { setToastMessage } from "../../store/slices/app/appSlice";
import {
  useGetInvoiceClientsQuery,
  useGetInvoiceIssuersQuery,
  useGetInvoicesQuery,
  useDeleteInvoiceMutation,
} from "../../store/apis/invoiceApi";
import { useGetOcOrdersQuery } from "../../store/apis/ocApi";
import OcOrderSelector, {
  OcOrderSelectorItem,
} from "../../components/invoices/OcOrderSelector";
import ClientFilterSelector from "../../components/common/ClientFilterSelector";
import { useUserRole } from "../../hooks/useUserRole";
import { HoldedStatus, InvoiceListItem } from "../../types/invoice";
import { formatEUR, formatDateES } from "../../utils/oc/ocCalc";
import PageBreadcrumbs from "../../components/layout/PageBreadcrumbs";

const HOLDED_STATUSES: HoldedStatus[] = ["PENDING", "SENT", "ERROR"];

// Badge de estado Holded: SENT=verde, PENDING=gris, ERROR=rojo.
const HoldedStatusChip: React.FC<{ status: HoldedStatus }> = ({ status }) => {
  const { t } = useTranslation();
  const color: "success" | "default" | "error" =
    status === "SENT" ? "success" : status === "ERROR" ? "error" : "default";
  return (
    <Chip
      size="small"
      color={color}
      label={t(`invoices.holdedStatus.${status}`)}
      sx={{ fontWeight: 600 }}
    />
  );
};

const InvoiceHistory: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const collapsed = useSelector(selectSidebarCollapsed);
  const { isAdmin } = useUserRole();

  // ---- Filtros ----
  const [clientId, setClientId] = useState("");
  const [issuerCompanyId, setIssuerCompanyId] = useState("");
  const [ocOrderId, setOcOrderId] = useState("");
  const [holdedStatus, setHoldedStatus] = useState<HoldedStatus | "">("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [search, setSearch] = useState("");

  // ---- Paginación (MUI 0-indexed; backend 1-indexed) ----
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);

  // ---- Orden de columnas (client-side: /invoices no acepta sortBy/sortOrder,
  // así que reordena la página ya cargada). Por defecto, más reciente primero.
  type SortKey =
    | "invoiceDate"
    | "clientLabel"
    | "issuerLabel"
    | "ocNumber"
    | "concept"
    | "total"
    | "holdedStatus"
    | "holdedDocNumber"
    | "createdByName";
  const [sortKey, setSortKey] = useState<SortKey>("invoiceDate");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDirection("asc");
    }
  };

  const { data: clientsResp } = useGetInvoiceClientsQuery();
  const { data: issuersResp } = useGetInvoiceIssuersQuery();
  const { data: ocOrdersResp } = useGetOcOrdersQuery({
    page: 1,
    pageSize: 500,
  });
  const clients = useMemo(() => clientsResp?.data ?? [], [clientsResp]);
  // El filtro de cliente opera a nivel empresa: deduplicamos los billings por
  // companyId para listar empresas únicas (el filtro envía clientId = companyId).
  const clientCompanies = useMemo(() => {
    const seen = new Map<string, { companyId: string; companyLabel: string }>();
    for (const c of clients) {
      if (!seen.has(c.companyId)) {
        seen.set(c.companyId, {
          companyId: c.companyId,
          companyLabel: c.companyLabel,
        });
      }
    }
    return Array.from(seen.values()).sort((a, b) =>
      a.companyLabel.localeCompare(b.companyLabel, "es")
    );
  }, [clients]);
  const clientOptions = useMemo(
    () =>
      clientCompanies.map((c) => ({ id: c.companyId, label: c.companyLabel })),
    [clientCompanies]
  );
  const issuers = useMemo(() => issuersResp?.data ?? [], [issuersResp]);
  const ocOrders = useMemo(
    () => ocOrdersResp?.data?.items ?? [],
    [ocOrdersResp]
  );

  const { data: invoicesResp, isFetching } = useGetInvoicesQuery({
    page: page + 1,
    pageSize,
    clientId: clientId || undefined,
    issuerCompanyId: issuerCompanyId || undefined,
    ocOrderId: ocOrderId || undefined,
    holdedStatus: holdedStatus || undefined,
    startDate: startDate || undefined,
    endDate: endDate || undefined,
    search: search || undefined,
  });

  // Memoizado porque alimenta el useMemo de ordenación: el `?? []` crearía un
  // array nuevo en cada render y lo recalcularía siempre.
  const invoices = useMemo(
    () => invoicesResp?.data?.items ?? [],
    [invoicesResp?.data?.items]
  );
  const total = invoicesResp?.data?.total ?? 0;

  const sortedInvoices = useMemo(() => {
    const getSortValue = (
      inv: InvoiceListItem,
      key: SortKey
    ): string | number => {
      switch (key) {
        case "invoiceDate":
          return inv.invoiceDate ? new Date(inv.invoiceDate).getTime() : 0;
        case "clientLabel":
          return inv.clientLabel ?? "";
        case "issuerLabel":
          return inv.issuerLabel ?? "";
        case "ocNumber":
          return inv.ocNumber ?? "";
        case "concept":
          return inv.concept ?? "";
        case "total":
          return inv.total ?? 0;
        case "holdedStatus":
          return t(`invoices.holdedStatus.${inv.holdedStatus}`) ?? "";
        case "holdedDocNumber":
          return inv.holdedDocNumber ?? "";
        case "createdByName":
          return inv.createdByName ?? "";
      }
    };

    return [...invoices].sort((a, b) => {
      const va = getSortValue(a, sortKey);
      const vb = getSortValue(b, sortKey);

      // Filas sin valor (OC, concepto o nº Holded vacíos) siempre al final,
      // sea cual sea la dirección del orden.
      const aEmpty = va === "" || va == null;
      const bEmpty = vb === "" || vb == null;
      if (aEmpty && !bEmpty) return 1;
      if (bEmpty && !aEmpty) return -1;
      if (aEmpty && bEmpty) return 0;

      const cmp =
        typeof va === "number" && typeof vb === "number"
          ? va - vb
          : String(va).localeCompare(String(vb), undefined, {
              sensitivity: "base",
            });

      return sortDirection === "asc" ? cmp : -cmp;
    });
  }, [invoices, sortKey, sortDirection, t]);

  // ---- Borrar (solo Admin) ----
  const [deleteInvoice, { isLoading: isDeleting }] = useDeleteInvoiceMutation();
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const handleConfirmDelete = async () => {
    if (!confirmDeleteId) return;
    try {
      await deleteInvoice(confirmDeleteId).unwrap();
      dispatch(
        setToastMessage({
          message: t("invoices.history.deleted"),
          severity: "success",
        })
      );
    } catch (e: any) {
      dispatch(
        setToastMessage({
          message: e?.data?.message || t("invoices.history.deleteError"),
          severity: "error",
        })
      );
    } finally {
      setConfirmDeleteId(null);
    }
  };

  const clearFilters = () => {
    setClientId("");
    setIssuerCompanyId("");
    setOcOrderId("");
    setHoldedStatus("");
    setStartDate("");
    setEndDate("");
    setSearch("");
    setPage(0);
  };

  const hasFilters =
    clientId ||
    issuerCompanyId ||
    ocOrderId ||
    holdedStatus ||
    startDate ||
    endDate ||
    search;

  // 9 columnas base + "Acciones" cuando el usuario es Admin
  const colCount = isAdmin ? 10 : 9;

  return (
    <Box sx={{ display: "flex" }}>
      <Box
        sx={{
          flexGrow: 1,
          p: 3,
          transition: "margin-left 0.3s ease",
          marginLeft: collapsed ? "64px" : "250px",
        }}
      >
        <PageBreadcrumbs />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h3">{t("invoices.history.title")}</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate("/invoices")}
            sx={{ textTransform: "none" }}
          >
            {t("invoices.history.newInvoice")}
          </Button>
        </Box>

        {/* Filtros */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2}>
            {/* Fila 1 — los dos selectores propios, que llevan la etiqueta
                encima del control. Van juntos para que no queden desalineados
                respecto a los campos con etiqueta flotante de la fila 2. */}
            <Grid item xs={12} md={6}>
              <Typography
                variant="pSmallest"
                sx={{ mb: 0.5, display: "block" }}
                color={theme.palette.neutral["600"]}
              >
                {t("invoices.history.filterClient")}
              </Typography>
              <ClientFilterSelector
                options={clientOptions}
                value={clientId}
                allLabel={t("invoices.history.all")}
                onChange={(id) => {
                  setClientId(id);
                  setPage(0);
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography
                variant="pSmallest"
                sx={{ mb: 0.5, display: "block" }}
                color={theme.palette.neutral["600"]}
              >
                {t("invoices.history.filterOc")}
              </Typography>
              <OcOrderSelector
                orders={ocOrders}
                selectedId={ocOrderId || null}
                onPick={(o: OcOrderSelectorItem) => {
                  setOcOrderId(o.id);
                  setPage(0);
                }}
                onClear={() => {
                  setOcOrderId("");
                  setPage(0);
                }}
              />
            </Grid>

            {/* Fila 2 — campos con etiqueta flotante. Las fechas van a ancho
                fijo (solo necesitan dd/mm/aaaa) y la búsqueda absorbe el
                espacio sobrante con `md` sin valor. */}
            <Grid item xs={12} md={3}>
              <TextField
                select
                label={t("invoices.history.filterIssuer")}
                fullWidth
                size="small"
                value={issuerCompanyId}
                onChange={(e) => {
                  setIssuerCompanyId(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="">{t("invoices.history.allFem")}</MenuItem>
                {issuers.map((i) => (
                  <MenuItem key={i.id} value={i.id}>
                    {i.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                select
                label={t("invoices.history.filterStatus")}
                fullWidth
                size="small"
                value={holdedStatus}
                onChange={(e) => {
                  setHoldedStatus(e.target.value as HoldedStatus | "");
                  setPage(0);
                }}
              >
                <MenuItem value="">{t("invoices.history.all")}</MenuItem>
                {HOLDED_STATUSES.map((s) => (
                  <MenuItem key={s} value={s}>
                    {t(`invoices.holdedStatus.${s}`)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography
                variant="pSmallest"
                sx={{ mb: 0.5, display: "block" }}
                color={theme.palette.neutral["600"]}
              >
                {t("invoices.history.filterOc")}
              </Typography>
              <OcOrderSelector
                orders={ocOrders}
                selectedId={ocOrderId || null}
                onPick={(o: OcOrderSelectorItem) => {
                  setOcOrderId(o.id);
                  setPage(0);
                }}
                onClear={() => {
                  setOcOrderId("");
                  setPage(0);
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label={t("invoices.history.searchPlaceholder")}
                fullWidth
                size="small"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(0);
                }}
              />
            </Grid>
            <Grid item xs={6} md="auto">
              <TextField
                label={t("invoices.history.from")}
                type="date"
                size="small"
                InputLabelProps={{ shrink: true }}
                sx={{ width: { xs: "100%", md: 170 } }}
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  setPage(0);
                }}
              />
            </Grid>
            <Grid item xs={6} md="auto">
              <TextField
                label={t("invoices.history.to")}
                type="date"
                size="small"
                InputLabelProps={{ shrink: true }}
                sx={{ width: { xs: "100%", md: 170 } }}
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  setPage(0);
                }}
              />
            </Grid>
            {hasFilters && (
              <Grid
                item
                xs={12}
                md="auto"
                sx={{ display: "flex", alignItems: "center" }}
              >
                <Button onClick={clearFilters}>
                  {t("invoices.history.clearFilters")}
                </Button>
              </Grid>
            )}
          </Grid>
        </Paper>

        {/* Tabla */}
        <Paper>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow
                  sx={{
                    "& th": {
                      fontWeight: 700,
                      bgcolor: theme.palette.neutral["100"],
                    },
                  }}
                >
                  <TableCell>
                    <TableSortLabel
                      active={sortKey === "invoiceDate"}
                      direction={
                        sortKey === "invoiceDate" ? sortDirection : "asc"
                      }
                      onClick={() => handleSort("invoiceDate")}
                    >
                      {t("invoices.history.colDate")}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortKey === "clientLabel"}
                      direction={
                        sortKey === "clientLabel" ? sortDirection : "asc"
                      }
                      onClick={() => handleSort("clientLabel")}
                    >
                      {t("invoices.history.colClient")}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortKey === "issuerLabel"}
                      direction={
                        sortKey === "issuerLabel" ? sortDirection : "asc"
                      }
                      onClick={() => handleSort("issuerLabel")}
                    >
                      {t("invoices.history.colIssuer")}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortKey === "ocNumber"}
                      direction={sortKey === "ocNumber" ? sortDirection : "asc"}
                      onClick={() => handleSort("ocNumber")}
                    >
                      {t("invoices.history.colOcNumber")}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortKey === "concept"}
                      direction={sortKey === "concept" ? sortDirection : "asc"}
                      onClick={() => handleSort("concept")}
                    >
                      {t("invoices.history.colConcept")}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={sortKey === "total"}
                      direction={sortKey === "total" ? sortDirection : "asc"}
                      onClick={() => handleSort("total")}
                    >
                      {t("invoices.history.colTotal")}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center">
                    <TableSortLabel
                      active={sortKey === "holdedStatus"}
                      direction={
                        sortKey === "holdedStatus" ? sortDirection : "asc"
                      }
                      onClick={() => handleSort("holdedStatus")}
                    >
                      {t("invoices.history.colStatus")}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortKey === "holdedDocNumber"}
                      direction={
                        sortKey === "holdedDocNumber" ? sortDirection : "asc"
                      }
                      onClick={() => handleSort("holdedDocNumber")}
                    >
                      {t("invoices.history.colHoldedNumber")}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortKey === "createdByName"}
                      direction={
                        sortKey === "createdByName" ? sortDirection : "asc"
                      }
                      onClick={() => handleSort("createdByName")}
                    >
                      {t("invoices.history.colCreatedBy")}
                    </TableSortLabel>
                  </TableCell>
                  {isAdmin && (
                    <TableCell align="center">
                      {t("invoices.history.colActions")}
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {isFetching ? (
                  <TableRow>
                    <TableCell colSpan={colCount} align="center" sx={{ py: 4 }}>
                      <OrbLoader
                        size="large"
                        label={t("common.loading", {
                          defaultValue: "Cargando…",
                        })}
                      />
                    </TableCell>
                  </TableRow>
                ) : invoices.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={colCount} align="center" sx={{ py: 4 }}>
                      <Typography
                        variant="pSmall"
                        color={theme.palette.neutral["600"]}
                      >
                        {t("invoices.history.empty")}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedInvoices.map((inv) => (
                    <TableRow
                      key={inv.id}
                      hover
                      sx={{ cursor: "pointer" }}
                      onClick={() => navigate(`/invoices/${inv.id}`)}
                    >
                      <TableCell>{formatDateES(inv.invoiceDate)}</TableCell>
                      <TableCell>{inv.clientLabel}</TableCell>
                      <TableCell>{inv.issuerLabel}</TableCell>
                      <TableCell>{inv.ocNumber || "—"}</TableCell>
                      <TableCell>{inv.concept || "—"}</TableCell>
                      <TableCell align="right">
                        {formatEUR(inv.total)}
                      </TableCell>
                      <TableCell align="center">
                        <HoldedStatusChip status={inv.holdedStatus} />
                      </TableCell>
                      <TableCell>{inv.holdedDocNumber || "—"}</TableCell>
                      <TableCell>{inv.createdByName || "—"}</TableCell>
                      {isAdmin && (
                        <TableCell
                          align="center"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Tooltip title={t("invoices.history.deleteInvoice")}>
                            <span>
                              <IconButton
                                size="small"
                                color="error"
                                disabled={isDeleting}
                                onClick={() => setConfirmDeleteId(inv.id)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_e, p) => setPage(p)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) => {
              setPageSize(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[25, 50, 100, 200]}
            labelRowsPerPage={t("common.rowsPerPage")}
          />
        </Paper>
      </Box>

      <ConfirmationModal
        open={confirmDeleteId !== null}
        title={t("invoices.history.deleteTitle")}
        message={t("invoices.history.deleteMessage")}
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmDeleteId(null)}
        isLoading={isDeleting}
      />
    </Box>
  );
};

export default InvoiceHistory;
